import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider, useLanguageContext } from './contexts/LanguageContext';
import { getImageUrl } from './config';
import HomePage from './pages/HomePage';
import WorshipPage from './pages/WorshipPage';
import DonationPage from './pages/DonationPage';
import PaymentResultPage from './pages/PaymentResultPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPageEN from './pages/TermsPageEN';
import PrivacyPageEN from './pages/PrivacyPageEN';
import Footer from './components/Footer';
import LanguageToggle from './components/LanguageToggle';
import VideoBackground from './components/VideoBackground';
import WorshipReviewPage from './pages/WorshipReviewPage';
import AdminDashboard from './admin/components/AdminDashboard';
import AdminLogin from './admin/components/AdminLogin';

// 버튼 공통 White & Brown 톤
const BUTTON_BROWN = '#6F4E37';
const BUTTON_BROWN_DARK = '#5A3E2B';
const BUTTON_CREAM = '#F5EDE3';

// Holy and Minimal 테마 설정
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#6B73FF',
      light: '#9C88FF',
      dark: '#5A52FF',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FF6B9D',
      light: '#FF88B5',
      dark: '#FF5388',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFBFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2D3748',
      secondary: '#718096',
    },
  },
  typography: {
    // 본문: 영문 Nunito Sans / 한글 Gowun Dodum (글자 단위 자동 적용)
    fontFamily: '"Nunito Sans", "Gowun Dodum", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Nunito", "Noto Sans KR", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: '"Nunito", "Noto Sans KR", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: '"Nunito", "Noto Sans KR", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: '"Nunito", "Noto Sans KR", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"Nunito", "Noto Sans KR", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    h6: {
      fontFamily: '"Nunito", "Noto Sans KR", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          },
        },
        // color prop(primary/secondary/warning 등)과 무관하게 동일한 톤 적용
        contained: {
          backgroundColor: BUTTON_BROWN,
          color: '#FFFFFF',
          border: '1px solid rgba(255, 255, 255, 0.7)',
          '&:hover': {
            backgroundColor: BUTTON_BROWN_DARK,
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(111, 78, 55, 0.45)',
            color: 'rgba(255, 255, 255, 0.6)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
        },
        outlined: {
          color: '#FFFFFF',
          borderColor: '#FFFFFF',
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          '&:hover': {
            color: BUTTON_BROWN,
            borderColor: '#FFFFFF',
            backgroundColor: '#FFFFFF',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.5)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          // 흰 배경 다이얼로그 안에서는 흰 글씨가 보이지 않으므로 갈색으로 반전
          '.MuiDialog-paper &': {
            color: BUTTON_BROWN,
            borderColor: BUTTON_BROWN,
            backgroundColor: 'transparent',
          },
          '.MuiDialog-paper &:hover': {
            color: BUTTON_BROWN_DARK,
            borderColor: BUTTON_BROWN_DARK,
            backgroundColor: BUTTON_CREAM,
          },
        },
        text: {
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
          },
          '.MuiDialog-paper &': {
            color: BUTTON_BROWN,
          },
          '.MuiDialog-paper &:hover': {
            backgroundColor: BUTTON_CREAM,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: 'transparent',
          color: '#FFFFFF',
          boxShadow: 'none',
          border: '2px solid white',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
  },
});

function App() {
  // Preload common background assets once to reduce first-paint flicker across pages
  useEffect(() => {
    const preload = (src: string) => {
      const img = new Image();
      img.src = src;
    };
    preload(getImageUrl('bg_image.png'));
    preload(getImageUrl('offering_box.png'));
    preload(getImageUrl('hands.png'));
  }, []);

  return (
    <HelmetProvider>
    <LanguageProvider>
      <TitleSetter />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: '100vh',
          }}
        >
        {/* 배경 비디오 - 무한 루프 재생 */}
        <VideoBackground />
        {/* Language Toggle - Fixed position */}
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1000,
          }}
        >
          <LanguageToggle variant="light" size="medium" />
        </Box>
        
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <RefreshRedirect />
          <Box sx={{ flex: 1 }} role="main" tabIndex={-1}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/worship" element={<WorshipPage />} />
              <Route path="/worship-review" element={<WorshipReviewPage />} />
              <Route path="/donation" element={<DonationPage />} />
              <Route path="/payment-result" element={<PaymentResultPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms-en" element={<TermsPageEN />} />
              <Route path="/privacy-en" element={<PrivacyPageEN />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/login" element={<AdminLogin />} />
            </Routes>
          </Box>
          <Footer />
        </Router>
      </Box>
    </ThemeProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App; 

// Sets the document title based on current UI language
function TitleSetter() {
  const { currentLanguage } = useLanguageContext();
  useEffect(() => {
    const ko = 'He Listens to Me | 맞춤형 온라인 교회';
    const en = 'He Listens to Me | Personalized Online Church';
    document.title = currentLanguage === 'en' ? en : ko;
  }, [currentLanguage]);
  return null;
}

// Restores scroll to top and moves keyboard focus to main on route changes
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const main = document.querySelector('[role="main"]') as HTMLElement | null;
    if (main) main.focus({ preventScroll: true } as any);
  }, [location.pathname]);
  return null;
}

// Redirects to homepage on page refresh (except if already on homepage or admin page)
function RefreshRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Pages that should NOT redirect to homepage on refresh
    const excludedPaths = ['/', '/admin', '/admin/login'];
    
    // Check if this is a page refresh (not initial navigation)
    const isPageRefresh = 
      window.performance && 
      window.performance.navigation && 
      window.performance.navigation.type === 1;
    
    // Alternative method for modern browsers
    const isPageReload = 
      window.performance &&
      window.performance.getEntriesByType &&
      window.performance.getEntriesByType('navigation')[0] &&
      (window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).type === 'reload';

    // If page was refreshed and not on excluded pages, redirect to homepage
    if ((isPageRefresh || isPageReload) && !excludedPaths.includes(location.pathname)) {
      console.log('Page refresh detected, redirecting to homepage...');
      navigate('/', { replace: true });
    }
  }, []); // Empty dependency array - only run once on mount

  return null;
}